import { Injectable } from '@angular/core';

export type TrendUnit = 'day' | 'week' | 'month' | 'quarter' | 'year' | 'hour';

export interface TrendResult {
  value: string;
  direction: 'up' | 'down' | 'stable';
  icon: 'trendingUp' | 'trendingDown' | 'minus';
  colorClass: string;
}

export interface RiskResult {
  label: 'CRITICAL' | 'HIGH' | 'MODERATE';
  colorClass: string;
}

export interface TimingInsight {
  label: string;
  value: string;
  status: 'normal' | 'warning' | 'critical';
}

@Injectable({
  providedIn: 'root'
})
export class TrendsCalculationService {

  /**
   * Calculates insights based on recovery timing data (polar chart)
   */
  calculateTimingInsights(data: { label: string, value: number }[]): TimingInsight[] {
    if (!data || data.length === 0) return [];

    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return [];

    // Find the highest and lowest performers (by percentage)
    const sorted = [...data].sort((a, b) => b.value - a.value);
    const highestLabel = sorted[0].label;
    const lowestLabel = sorted[sorted.length - 1].label;

    return data.map(item => {
      const percentage = Math.round((item.value / total) * 100);
      let descriptiveLabel = '';
      let status: 'normal' | 'warning' | 'critical' = 'normal';

      // Assign descriptive text based on position and value
      if (item.label === highestLabel) {
        descriptiveLabel = '(highest recovery, urgent cases)';
      } else if (item.label === lowestLabel) {
        descriptiveLabel = '⚠️ (lowest recovery, key leakage zone)';
        status = 'warning';
      } else if (percentage > 20) {
        descriptiveLabel = '(moderate recovery)';
      } else if (percentage > 10) {
        descriptiveLabel = '(stable but not optimal)';
      } else {
        descriptiveLabel = '(high value but risky / delayed)';
      }

      return {
        label: `${item.label}:`,
        value: `${percentage}% ${descriptiveLabel}`,
        status: status
      };
    });
  }

  /**
   * Calculates percentage growth/decline
   * Recovered YoY % = (Curr - Prev) / Prev
   */
  calculatePercentageChange(current: number, previous: number): number | null {
    if (!previous || previous === 0) return null;
    return (current - previous) / previous;
  }

  /**
   * Calculates absolute difference in percentage points
   * Success Rate YoY (pp) = Curr - Prev
   */
  calculatePPChange(current: number, previous: number): number | null {
    if (current === undefined || previous === undefined) return null;
    return current - previous;
  }

  /**
   * Generates dynamic trend text and styling
   * Format: [Arrow] [Value]% [pp] [Unit Comparison]
   */
  getTrendResult(change: number | null, unit: TrendUnit, isPp: boolean = false, isInverse: boolean = false, duration?: number): TrendResult | null {
    if (change === null) return null;

    const absChange = Math.abs(change);
    const percentageValue = isPp ? (absChange * 100).toFixed(1) : Math.round(absChange * 100);
    const direction = change > 0 ? 'up' : (change < 0 ? 'down' : 'stable');
    const unitLabel = this.getPeriodLabel(duration, unit);
    const ppSuffix = isPp ? ' pp' : '';

    const valueText = `${percentageValue}%${ppSuffix} ${unitLabel}`;

    return {
      value: valueText,
      direction: direction,
      icon: this.getTrendIcon(direction, isInverse),
      colorClass: this.getTrendColorClass(direction, isInverse)
    };
  }

  /**
   * Determines the icon based on direction.
   * Arrows always follow the numeric direction regardless of metric type.
   */
  private getTrendIcon(direction: 'up' | 'down' | 'stable', isInverse: boolean): 'trendingUp' | 'trendingDown' | 'minus' {
    if (direction === 'stable') return 'minus';
    return direction === 'up' ? 'trendingUp' : 'trendingDown';
  }

  /**
   * Calculates non-recovered share and returns risk level
   * Lost Risk Label logic: >0.5 Critical, >0.3 High, else Moderate
   * If trend is improving (downward for non-recovered), it reflects this.
   */
  getRiskLevel(nonRecovered: number, scheduled: number, direction?: 'up' | 'down' | 'stable'): RiskResult {
    if (direction === 'down') {
      return { label: 'IMPROVING' as any, colorClass: 'text-emerald-500' };
    }

    if (!scheduled || scheduled === 0) return { label: 'MODERATE', colorClass: 'text-emerald-500' };

    const share = nonRecovered / scheduled;

    if (share > 0.5) {
      return { label: 'CRITICAL', colorClass: 'text-rose-500' };
    } else if (share > 0.3) {
      return { label: 'HIGH', colorClass: 'text-orange-500' };
    }
    
    return { label: 'MODERATE', colorClass: 'text-emerald-500' };
  }

  /**
   * Generates a descriptive footer for the recovery amount card
   */
  getRecoveryFooter(change: number | null): string {
    if (change === null) return 'Awaiting period comparison';
    if (change > 0) return 'Growth on target';
    if (change < -0.1) return 'Significant decline in recovery';
    if (change < 0) return 'Below expected target';
    return 'Stable recovery outcomes';
  }

  /**
   * Generates a descriptive footer for the recovery rate percentage card
   */
  getRecoveryRateFooter(ppChange: number | null): string {
    if (ppChange === null) return 'Awaiting period comparison';
    if (ppChange > 0.02) return 'Significant margin improvement';
    if (ppChange > 0) return 'Improving margins';
    if (ppChange < -0.02) return 'Critical margin compression';
    if (ppChange < 0) return 'Slight margin decline';
    return 'Stable margins';
  }

  /**
   * Maps unit to shorthand comparison label or dynamic period label based on duration
   */
  private getPeriodLabel(duration: number | undefined, unit: TrendUnit): string {
    if (duration === undefined) {
      return this.getUnitShorthand(unit);
    }

    if (duration === 1) return 'DoD';
    if (duration === 7) return 'WoW';
    if (duration >= 28 && duration <= 31) return 'MoM';
    if (duration >= 89 && duration <= 92) return 'QoQ';
    if (duration >= 364 && duration <= 366) return 'YoY';

    return `vs Previous ${duration} Days`;
  }

  /**
   * Maps unit to shorthand comparison label
   */
  private getUnitShorthand(unit: TrendUnit): string {
    switch (unit) {
      case 'year': return 'YoY';
      case 'quarter': return 'QoQ';
      case 'month': return 'MoM';
      case 'week': return 'WoW';
      case 'day': return 'DoD';
      default: return `${unit} over ${unit}`;
    }
  }

  /**
   * Returns consistent tailwind classes for trend colors
   */
  private getTrendColorClass(direction: 'up' | 'down' | 'stable', isInverse: boolean = false): string {
    if (direction === 'stable') return 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400';

    const positiveClass = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400';
    const negativeClass = 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400';

    if (isInverse) {
      return direction === 'up' ? negativeClass : positiveClass;
    }

    return direction === 'up' ? positiveClass : negativeClass;
  }
}
